import React, { Suspense, lazy } from 'react';
import PropTypes from 'prop-types';
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
  Navigate,
} from 'react-router-dom';
import AppShell, { AnimatedOutlet } from 'components/DigitalCECB/Common';
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { fetchWithAuth, handleApiError } from 'helpers/fetchWithAuth';
import staticCecbData from 'components/DigitalCECB/cecbData.json';

function DigitalCECBShell() {
  const { cecbPlusId } = useParams();
  const [cecbData, setCecbData] = useState(cecbPlusId ? null : staticCecbData);
  const [loading, setLoading] = useState(!!cecbPlusId);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!cecbPlusId) return;

    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await fetchWithAuth(
          'Fetching CECB+ data',
          `/cecbplus-digital/${cecbPlusId}/get-official-json/`,
        );
        if (!cancelled) setCecbData(data);
      } catch (err) {
        handleApiError(err, 'Failed to fetch CECB+ data');
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [cecbPlusId, reloadKey]);

  if (loading) {
    return (
      <div className="cecb-shell-state">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="cecb-shell-state cecb-shell-state--error">
        <p>Impossible de charger les données du rapport CECB+.</p>
        <button
          className="btn btn--primary"
          onClick={() => setReloadKey((k) => k + 1)}
        >
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <AppShell cecbData={cecbData}>
      <AnimatedOutlet />
    </AppShell>
  );
}
import './app-dark.css';
import { useAuth } from 'helpers/AuthProvider';
import SideNavBar from 'components/Layout/SideNavBar';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import LoadingSpinner from 'components/ReusableComponents/LoadingSpinner';
import ProtectedRoute from 'helpers/ProtectedRoute';
import { HelmetProvider } from 'react-helmet-async';
import { AnimatePresence } from 'framer-motion';
import PageWrapper from 'components/FramerMotion/PageWrapper';

// Dynamic Imports
const SignIn = lazy(() => import('./components/LoggingOperations/SignIn'));
const Logout = lazy(() => import('./components/LoggingOperations/LogOut'));
const TwoFAVerifyPage = lazy(
  () => import('./components/LoggingOperations/TwoFAVerifyPage'),
);
const ResetPasswordRequest = lazy(
  () => import('./components/LoggingOperations/ResetPasswordRequest'),
);
const ResetPassword = lazy(
  () => import('./components/LoggingOperations/ResetPassword'),
);
const OneTimeLogin = lazy(
  () => import('./components/LoggingOperations/OneTimeLogin'),
);
const ProductList = lazy(() => import('./components/ModelsList/ProductList'));
const CustomerProjectForm = lazy(
  () => import('./components/Pages/CustomerProjectForm'),
);
const MessagePage = lazy(() => import('./components/Pages/MessagePage'));
const DigitalCI = lazy(() => import('./components/DigitalCI/DigitalCI'));
const DigitalCECBDashboard = lazy(
  () => import('./components/DigitalCECB/Dashboard'),
);
const DigitalCECBEtatInitial = lazy(
  () => import('./components/DigitalCECB/EtatInitial'),
);
const DigitalCECBComposants = lazy(
  () => import('./components/DigitalCECB/ComposantsDetail'),
);
const DigitalCECBVariantesOverview = lazy(
  () => import('./components/DigitalCECB/VariantesOverview'),
);
const DigitalCECBVarianteDetails = lazy(
  () => import('./components/DigitalCECB/VarianteDetails'),
);
const DigitalCECBSubsidyAndCosts = lazy(
  () => import('./components/DigitalCECB/SubsidyAndCosts'),
);
const DigitalCECBProcessSteps = lazy(
  () => import('./components/DigitalCECB/ProcessSteps'),
);
const StudioBatiment = lazy(() => import('./components/3D/StudioBatiment'));
const SharedBuildingPage = lazy(
  () => import('./components/3D/SharedBuildingPage'),
);
const MandateAdminNew = lazy(
  () => import('./components/product_admin_forms/MandateAdminNew'),
);
const Home = lazy(() => import('./components/Home/Home'));
const SustainabilityInfo = lazy(
  () => import('./components/Pages/DataProtection'),
);
const ProductCrud = lazy(() => import('./components/Pages/ProductCrud'));
const BuildingForm = lazy(() => import('./components/ModelsForm/BuildingForm'));
const AdminHome = lazy(() => import('./components/Home/AdminHome'));
const MyBuildings = lazy(() => import('./components/ModelsList/BuildingsList'));
const UsersList = lazy(() => import('./components/ModelsList/UsersList'));
const QuotesList = lazy(() => import('./components/ModelsList/QuotesList'));
const ProspectorList = lazy(
  () => import('./components/ModelsList/ProspectorList'),
);
const UserForm = lazy(() => import('./components/ModelsForm/UserForm'));
const QuoteForm = lazy(() => import('./components/ModelsForm/QuoteForm'));
const ProspectionCampaignForm = lazy(
  () => import('./components/ModelsForm/ProspectionCampaignForm'),
);
const PartnerHome = lazy(() => import('./components/Home/PartnerHome'));
const MandateFormRouter = lazy(
  () => import('./components/product_admin_forms/MandateFormRouter.jsx'),
);
const CustomerHome = lazy(() => import('./components/Home/CustomerHome'));
const SettingsPage = lazy(() => import('./components/Settings/Settings'));
const NotFoundPage = lazy(() => import('components/Pages/NotFoundPage'));
const Source = lazy(() => import('components/Source/MainSourcePage'));

// Route definitions (module-level to avoid re-creation on each render)
const publicRoutes = [
  { path: '/logout', element: <Logout /> },
  { path: '/signin', element: <SignIn /> },
  { path: '/2fa-verify', element: <TwoFAVerifyPage /> },
  { path: '/password-reset-request', element: <ResetPasswordRequest /> },
  { path: '/login', element: <OneTimeLogin /> },
  {
    path: '/password-reset',
    element: <ResetPassword needCurrentPw={false} />,
  },
  {
    path: '/digitalci-mockup',
    element: <DigitalCI />,
  },
  { path: '/', element: <Home /> },
  { path: '/data-protection', element: <SustainabilityInfo /> },
  { path: '/message', element: <MessagePage /> },
  { path: '/kick-off-form/:uuid', element: <CustomerProjectForm /> },
  { path: '/not-found', element: <NotFoundPage /> },
  { path: '*', element: <Navigate to="/not-found" replace /> },
  {
    path: '/IncitativeConsulting/admin-form/:mandateId/digitalci',
    element: <DigitalCI />,
  },
  // Lien de partage 3D en lecture seule : sans connexion (jeton vérifié par le back)
  { path: '/three-d/view/:token', element: <SharedBuildingPage /> },
];

const restrictedRoutes = [
  {
    path: '/admin-home',
    element: <AdminHome />,
    requiredRoles: ['admin', 'employee'],
  },
  {
    path: '/users-list',
    element: <UsersList />,
    requiredRoles: ['admin', 'employee'],
  },
  {
    path: '/users',
    element: <UserForm />,
    requiredRoles: ['admin', 'employee'],
  },
  {
    path: '/quotes-list',
    element: <QuotesList />,
    requiredRoles: ['admin', 'employee'],
  },
  {
    path: '/users/:userId',
    element: <UserForm />,
    requiredRoles: ['admin', 'employee'],
  },
  {
    path: '/quotes/:id',
    element: <QuoteForm />,
    requiredRoles: ['admin', 'employee'],
  },
  {
    path: '/quotes',
    element: <QuoteForm />,
    requiredRoles: ['admin', 'employee'],
  },
  {
    path: '/new-building',
    element: <BuildingForm />,
    requiredRoles: ['admin', 'employee'],
  },
  {
    path: '/new-prospection-campaign',
    element: <ProspectionCampaignForm />,
    requiredRoles: ['admin'],
  },
  {
    path: '/prospection-campaign/:id',
    element: <ProspectionCampaignForm />,
    requiredRoles: ['admin'],
  },
  {
    path: '/buildings/:id',
    element: <BuildingForm />,
    requiredRoles: ['admin', 'employee'],
  },
  {
    path: '/buildings-list/',
    element: <MyBuildings />,
    requiredRoles: ['admin', 'employee'],
  },
  {
    path: '/mandates/:mandateType/new',
    element: <MandateAdminNew />,
    requiredRoles: ['admin', 'employee'],
  },
  {
    path: '/mandates/:mandateId/form/admin',
    element: <MandateFormRouter mode="admin" />,
    requiredRoles: ['admin', 'employee'],
  },
  {
    path: '/mandates/:mandateId/form/customer',
    element: <MandateFormRouter mode="customer" />,
    requiredRoles: ['admin', 'customer'],
  },
  {
    path: '/mandates/:mandateId/form/partner',
    element: <MandateFormRouter mode="partner" />,
    requiredRoles: ['admin', 'partner'],
  },
  {
    path: '/products-list',
    element: <ProductList />,
    requiredRoles: ['admin'],
  },
  {
    path: '/prospector',
    element: <ProspectorList />,
    requiredRoles: ['admin'],
  },
  {
    path: '/products/:id',
    element: <ProductCrud />,
    requiredRoles: ['admin'],
  },
  { path: '/products', element: <ProductCrud />, requiredRoles: ['admin'] },
  {
    path: '/partner-home',
    element: <PartnerHome />,
    requiredRoles: ['partner'],
  },
  {
    path: '/customer-home',
    element: <CustomerHome />,
    requiredRoles: ['customer'],
  },
  {
    path: '/source',
    element: <Source />,
    requiredRoles: ['admin', 'employee'],
  },
  {
    path: '/settings/',
    element: <SettingsPage />,
    requiredRoles: ['admin'],
  },
];

// Clé de transition racine. On garde `location.pathname` par défaut, SAUF pour
// le module DigitalCECB : toutes ses sous-pages partagent une clé stable, afin
// que le shell (et son AnimatedOutlet interne) ne soit pas remonté à chaque
// navigation entre pages du module — c'est lui qui anime ses transitions.
function routeTransitionKey(pathname) {
  if (pathname.startsWith('/digitalcecb')) return 'digitalcecb-module';
  return pathname;
}

function AnimatedRoutes({ publicRoutes, restrictedRoutes }) {
  const location = useLocation();

  return (
    <Suspense fallback={<LoadingSpinner loading={true} />}>
      <AnimatePresence mode="wait">
        <Routes location={location} key={routeTransitionKey(location.pathname)}>
          {publicRoutes.map((route) => (
            <Route
              key={route.path}
              path={route.path}
              element={<PageWrapper>{route.element}</PageWrapper>}
            />
          ))}

          <Route
            path="/three-d"
            element={
              <ProtectedRoute requiredRoles={['admin', 'employee']}>
                <StudioBatiment />
              </ProtectedRoute>
            }
          />

          {['/digitalcecb', '/digitalcecb/:cecbPlusId'].map((path) => (
            <Route
              key={path}
              path={path}
              element={
                /* Sans id : mockup public sur données statiques */
                path === '/digitalcecb' ? (
                  <DigitalCECBShell />
                ) : (
                  <ProtectedRoute
                    requiredRoles={['admin', 'employee', 'customer']}
                  >
                    <DigitalCECBShell />
                  </ProtectedRoute>
                )
              }
            >
              <Route index element={<DigitalCECBDashboard />} />
              <Route path="etat-initial" element={<DigitalCECBEtatInitial />} />
              <Route
                path="etat-initial/composants"
                element={<DigitalCECBComposants />}
              />
              <Route
                path="variantes"
                element={<DigitalCECBVariantesOverview />}
              />
              <Route
                path="variantes/:id"
                element={<DigitalCECBVarianteDetails />}
              />
              <Route
                path="subventions"
                element={<DigitalCECBSubsidyAndCosts />}
              />
              <Route
                path="marche-a-suivre"
                element={<DigitalCECBProcessSteps />}
              />
            </Route>
          ))}

          {restrictedRoutes.map((route) => (
            <Route
              key={route.path}
              path={route.path}
              element={
                <ProtectedRoute requiredRoles={route.requiredRoles}>
                  <PageWrapper>{route.element}</PageWrapper>
                </ProtectedRoute>
              }
            />
          ))}
        </Routes>
      </AnimatePresence>
    </Suspense>
  );
}

AnimatedRoutes.propTypes = {
  publicRoutes: PropTypes.arrayOf(PropTypes.object).isRequired,
  restrictedRoutes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

function App() {
  const { authState } = useAuth();

  return (
    <HelmetProvider>
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <div className="app">
          <SideNavBar authState={authState} />
          <div className="content">
            <AnimatedRoutes
              publicRoutes={publicRoutes}
              restrictedRoutes={restrictedRoutes}
            />
          </div>
          {!window.Cypress && (
            <ToastContainer position="top-right" autoClose={5000} />
          )}
        </div>
      </Router>
    </HelmetProvider>
  );
}

export default App;
